import React from "react";
import { ArrowRight, CheckCircle2, Monitor, Smartphone, Tablet, Laptop, Clock, Award } from "lucide-react";
import { courseModules } from "../data/mock";

const includes = [
  "Video demonstrations of the signs and phrases throughout",
  "Useful everyday vocabulary, including signs for home, education, healthcare, transport and work",
  "Certificate of completion for the CPD-certified course, with a verification code",
  "One-time payment. No subscription, nothing renews.",
  "Two years’ access to all ten sections and assessments, from the day you first log in.",
];

// Topic areas drawn directly from the official help article:
// https://help.british-sign.co.uk/en/article/how-is-the-course-delivered-what-format-is-the-course-in-1ifwfld/
const topics = [
  "Fingerspelling",
  "Numbers",
  "Colours",
  "Deaf Awareness & Audism",
  "Introductions",
  "Family",
  "Relationships",
  "Descriptions",
  "Hobbies",
  "Time",
  "Money",
  "Days, Months & Seasons",
  "Food & Drink",
  "Emotions",
  "Animals",
  "Clothing",
  "Short everyday exchanges",
];

const devices = [
  { Icon: Monitor, label: "Desktop" },
  { Icon: Laptop, label: "Laptop" },
  { Icon: Tablet, label: "Tablet" },
  { Icon: Smartphone, label: "Mobile" },
];

const CoursePreview = () => {
  return (
    <section className="bs-course" id="course">
      <div className="bs-container bs-course__grid">
        <div className="bs-course__left">
          <span className="bs-eyebrow">The course</span>
          <h2 className="bs-h2">
            Introducing British Sign Language
          </h2>
          <p className="bs-muted">
            The course is divided into ten sections. Each one has short
            explanations and video demonstrations of the signs, followed by a
            short assessment. Spend around two hours a week and most learners
            finish in about three months.
          </p>

          <div className="bs-course__commitments">
            <div className="bs-course__commit" data-testid="course-pace">
              <Clock size={18} />
              <div>
                <strong>Around 2 hours a week</strong>
                <span>Most finish in about three months</span>
              </div>
            </div>
            <div className="bs-course__commit" data-testid="course-passmark">
              <Award size={18} />
              <div>
                <strong>Pass mark: 85%</strong>
                <span>Retake any section assessment for free, as often as you like</span>
              </div>
            </div>
          </div>

          <ul className="bs-course__includes">
            {includes.map((it) => (
              <li key={it}>
                <CheckCircle2 size={18} />
                {it}
              </li>
            ))}
          </ul>

          <div className="bs-course__topics" data-testid="course-topics">
            <h3 className="bs-course__topics-title">What you’ll learn</h3>
            <ul className="bs-course__topics-list">
              {topics.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </div>

          <p className="bs-course__a11y" data-testid="course-a11y-note">
            No webcam needed. You don’t need to hear audio to complete the course.{" "}
            <a href="/enrol">
              See full course details
            </a>
            .
          </p>

          <div className="bs-course__devices" aria-label="Works on desktop, laptop, tablet and mobile">
            <span className="bs-course__deviceslabel">Works on desktop, laptop, tablet and mobile</span>
            <div className="bs-course__deviceicons">
              {devices.map(({ Icon, label }) => (
                <span className="bs-course__device" key={label} title={label}>
                  <Icon size={16} />
                  {label}
                </span>
              ))}
            </div>
          </div>

          <div className="bs-course__cta">
            <a
              href="/enrol"
              className="bs-btn bs-btn--solid"
              data-testid="course-enrol-btn"
            >
              Enrol · Pay What You Can
              <ArrowRight size={18} />
            </a>
            <span className="bs-course__guarantee">
              <span className="bs-course__price">From £5</span>
              <span>Regular price <span className="bs-course__strike">£30</span></span>
            </span>
          </div>
        </div>

        <div className="bs-course__right">
          <div className="bs-course__panel">
            <div className="bs-course__panelhead">
              <span className="bs-eyebrow bs-eyebrow--light">Course outline</span>
              <span className="bs-course__duration">10 sections</span>
            </div>
            <ol className="bs-modules">
              {/* Show the first five sections on the homepage. The full
                  curriculum lives on /enrol so this section stays tight
                  for a visitor who isn't ready to enrol yet. */}
              {courseModules.slice(0, 5).map((m, i) => (
                <li className="bs-module" key={`${m.title}-${i}`}>
                  <span className="bs-module__num">{m.num}</span>
                  <span className="bs-module__title">{m.title}</span>
                </li>
              ))}
              <li className="bs-module bs-module--more">
                <span className="bs-module__num">+5</span>
                <a
                  href="/enrol#curriculum"
                  className="bs-module__title bs-module__more-link"
                  data-testid="course-preview-see-all"
                >
                  See the full curriculum
                </a>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoursePreview;
